import * as React from 'react';
import { useState, useEffect, useRef, createContext  } from 'react';
import { withStyles } from '@mui/styles';
import {keyMap} from '~common/components/app-view/key-mapping';
import {
  WithStyles,
  StyleRules,
  ClassNameMap
} from '@mui/styles/withStyles';
import { each } from 'lodash'
import { Theme } from '@mui/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Hidden,
  Avatar,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import MenuDrawer from './menu-drawer';
import UserPrefsButton from '~common/components/app-view/user-prefs-button';
import LanguageButton from '~common/components/app-view/preferred-language-button';
import FloatingTrigger from '~common/components/floating-trigger';
import PortalUserContext from '~common/lib/portal-user-context';
import Faq from '~common/lib/models/faq';
import ErrorBoundary from "~common/components/error-boundary";
import HomeButton from "~common/components/home";
import AlertButton from "~common/components/alert";
import {
  theme,
  styleRules,
  logoFileName,
} from "~common/components/app-view/theme";
import * as request from "superagent";
var Mousetrap = require("mousetrap");
var mousetrap = new Mousetrap();
const styles = (theme: Theme): StyleRules => styleRules;
interface Props extends React.HTMLProps<HTMLDivElement> {
  history: any;
  areaKey: string;
  hideToolbar?: boolean;
  helpDocUrl?: string;
  isTokenUser?: boolean;
  onLanguageChange?: (func: Function) => void;
  classes?: ClassNameMap;
}
const ExpireDialog = (props: {
  title: string;
  children: React.ReactNode;
  open: boolean;
  onConfirm: () => void;
}) => {
  const { title, children, open, onConfirm } = props;
  return (
    <Dialog open={open} aria-labelledby="confirm-dialog">
      <DialogTitle id="confirm-dialog">{title}</DialogTitle>
      <DialogContent style={{ width: 450 }}>{children}</DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onConfirm}
          color="primary"
        >
          Extend
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export interface HotKeyMappingContextProps {
  currentKeyMap: any;
  setCurrentKeyMap: (keyMap: any) => void;
}
export const HotKeyMappingContext = createContext();
function AppView(props: Props & WithStyles<typeof styles>) {
  const {
    history,
    areaKey,
    isTokenUser,
    helpDocUrl: initialHelpDocUrl,
    hideToolbar,
    onLanguageChange,
    classes
  } = props;
  mousetrap.reset();
  each(keyMap, (entry: any, combo: any) => {
    mousetrap.bind(combo, function() {
      setCurrentKeyMap(entry);
    });
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpDocUrl, setHelpDocUrl] = useState(initialHelpDocUrl);
  const [isExpiring, setIsExpiring] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionExpireTime, setSessionExpireTime] = useState<Date | undefined>(undefined);
  const [currentKeyMap, setCurrentKeyMap] = useState(null);
  // We'll store interval and timeout IDs in refs so we can clear them on unmount
  const ExpireIntervalRef = useRef<any>(undefined);
  const ExpireTimeoutRef = useRef<any>(undefined);
  const isLoggedIn = PortalUserContext.isLoggedIn();
  const getWarningThresholdMinutes = React.useCallback(() => {
    let m = 2;
    if (
      (window as any).App &&
      (window as any).App.browserEnvironment &&
      (window as any).App.browserEnvironment.BROWSER_PORTAL_USER_SESSION_EXPIRE_ALERT_WAIT_MINUTES
    ) {
      m = Number((window as any).App.browserEnvironment.BROWSER_PORTAL_USER_SESSION_EXPIRE_ALERT_WAIT_MINUTES);
    }
    return m;
  }, []);
  const getWarningThresholdMessage = React.useCallback(() => {
    let msg: string = "Your session is about to expire.";
    if (
      (window as any).App &&
      (window as any).App.browserEnvironment &&
      (window as any).App.browserEnvironment.BROWSER_PORTAL_USER_SESSION_EXPIRE_ALERT_MESSAGE
    ) {
      msg = (window as any).App.browserEnvironment.BROWSER_PORTAL_USER_SESSION_EXPIRE_ALERT_MESSAGE;
    }
    return msg;
  }, []);
  const cancelExpiration = (doPost: boolean) => {
    if (doPost) {
      request.get("/session/extend").then(() => {});
    }
    if (isExpiring) {
      clearTimeout(ExpireTimeoutRef.current);
      clearInterval(ExpireIntervalRef.current);
      ExpireTimeoutRef.current = undefined;
      ExpireIntervalRef.current = undefined;
      setIsExpiring(false);
    }
  };
  const doCancelExpire = () => {
    cancelExpiration(true);
  };
  const checkExpiration = (cookieStores: any) => {
    if (
      (window as any).App &&
      (window as any).App.browserEnvironment &&
      (
        (window as any).App.browserEnvironment.BROWSER_PORTAL_USER_SESSION_RESET_ON_ACTIVE == "true" ||
        (window as any).App.browserEnvironment.BROWSER_PORTAL_USER_SESSION_EXPIRE_SHOW_TIMER == "true"
      )
    ) {
      let expiring = false;
      cookieStores.forEach((c: any) => {
        if (c.name === "portalExpire") {
          const thresholdMinutes = getWarningThresholdMinutes();
          expiring = new Date(new Date().getTime() + thresholdMinutes * 60000) > c.expires;
          if (expiring) {
            const expireAt = new Date(new Date().getTime() + thresholdMinutes * 60000);
            if (!isExpiring) {
              setIsExpiring(true);
              setSessionExpireTime(expireAt);
              // Set an interval to update UI if needed
              ExpireIntervalRef.current = window.setInterval(() => {
                // force an update if needed
                setTimeRemaining((prev) => prev + 1);
              }, thresholdMinutes * 1000);
              // Timeout to logout user if not extended
              ExpireTimeoutRef.current = window.setTimeout(() => {
                if (isExpiring) {
                  window.location.href = "/logout";
                }
              }, thresholdMinutes * 1000 * 60);
            }
          } else {
            // Not expiring
            cancelExpiration(false);
          }
        }
      });
    }
  };
  useEffect(() => {
    Mousetrap.bind("ctrl+alt+h", () => {
      window.location.href = "/";
    });
    const intervalId = window.setInterval(() => {
      const cs = (window as any).cookieStore;
      if (cs) {
        cs.getAll().then(checkExpiration);
      }
    }, 1000 * 5);
    return () => {
      // Cleanup on unmount
      window.clearInterval(intervalId);
      cancelExpiration(false);
      // If needed: Mousetrap.unbind("ctrl+alt+h");
    };
  }, [checkExpiration]);
  const handleLogin = () => {
    window.location.href = "/login";
  };
  const handleDrawerMenu = () => {
    setDrawerOpen(true);
  };
  const handleDrawerMenuClose = () => {
    setDrawerOpen(false);
  };
  const showLoginButton = !isLoggedIn && window.location.pathname !== "/login";
  let floatingHelpTrigger = null;
  if (helpDocUrl) {
    floatingHelpTrigger = (
      <FloatingTrigger faIconClass="fas fa-life-ring" url={helpDocUrl} />
    );
  }
  let floatingSuggestionTrigger = null;
  if (areaKey) {
    let right = 15;
    if (floatingHelpTrigger != null) {
      right = 90;
    }
    floatingSuggestionTrigger = (
      <FloatingTrigger
        faIconClass="fas fa-lightbulb"
        url={"/suggestions" + "/" + areaKey}
        right={right}
      />
    );
  }
  const footer = (
    <div className={classes.poweredBy} style={{width: '100%', position: 'fixed', bottom:5, textAlign: 'center' }}>
      <div onClick={()=>{window.open('https://icginnovations.com')}} style={{width: 'fit-content', margin: 'auto', cursor: 'pointer'}}>
        <img style={{width: 120}} src="/static/assets/powered-by.png" /><br/>
        <label style={{cursor: 'inherit'}}>Leading the way in transformative business technology solutions.</label>
      </div>
    </div>
  );
  let bottomPadding = null;
  if (floatingHelpTrigger || floatingSuggestionTrigger) {
    bottomPadding = <div className={classes.bottomPadding} />;
  }
  let countdown = null;
  let expiredModal = null;
  if (isExpiring) {
    if (
      (window as any).App &&
      (window as any).App.browserEnvironment &&
      (window as any).App.browserEnvironment.BROWSER_PORTAL_USER_SESSION_UI === "dialog"
    ) {
      expiredModal = (
        <ExpireDialog
          title="WARNING"
          open={isExpiring}
          onConfirm={() => {
            setIsExpiring(false);
            request.get("/session/extend").then(() => {});
          }}
        >
          {getWarningThresholdMessage()}
        </ExpireDialog>
      );
    } else {
      const remainingSeconds = sessionExpireTime ? Math.floor((sessionExpireTime.getTime() - new Date().getTime()) / 1000) : 0;
      countdown = (
        <span>
          <Chip
            avatar={
              <i
                style={{
                  fontSize: 20,
                  color: remainingSeconds > 60 ? "yellow" : "red",
                  paddingLeft: 10,
                  paddingTop: 3,
                }}
                className="fa fa-lightbulb"
              />
            }
            onClick={doCancelExpire}
            label={getWarningThresholdMessage()}
          />
        </span>
      );
    }
  }
  let sizer = null;
  if (window.location.hostname === "localhost" && isLoggedIn) {
    sizer = (
      <span>
        <Hidden only={["sm", "md", "lg", "xl"]}>
          <Chip avatar={<Avatar>XS</Avatar>} label="Extra Small" className={classes.chip}/>
        </Hidden>
        <Hidden only={["xs", "md", "lg", "xl"]}>
          <Chip avatar={<Avatar>SM</Avatar>} label="Small" className={classes.chip}/>
        </Hidden>
        <Hidden only={["xs", "sm", "lg", "xl"]}>
          <Chip avatar={<Avatar>MD</Avatar>} label="Medium" className={classes.chip}/>
        </Hidden>
        <Hidden only={["xs", "sm", "md", "xl"]}>
          <Chip avatar={<Avatar>LG</Avatar>} label="Large" className={classes.chip}/>
        </Hidden>
        <Hidden only={["xs", "sm", "md", "lg"]}>
          <Chip avatar={<Avatar>XL</Avatar>} label="Extra Large" className={classes.chip}/>
        </Hidden>
      </span>
    );
  }
  let showAlerts = false;
  if ((window as any).App && (window as any).App.browserEnvironment) {
    showAlerts = (window as any).App.browserEnvironment.BROWSER_ENABLE_ALERT_MODULE;
  }
  let appBarClassName = "appBar";
  let appBarStyle: React.CSSProperties | undefined;
  let containerClass = classes.root;
  if (hideToolbar) {
    appBarClassName += " appBarHidden";
    appBarStyle = {
      display: "none"
    };
    containerClass = classes.rootHidden;
  }
  return (
    <ErrorBoundary>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <HotKeyMappingContext.Provider value={[currentKeyMap, setCurrentKeyMap]}>
            <div className={containerClass}>
              <MenuDrawer
                open={drawerOpen}
                onClose={handleDrawerMenuClose}
                history={history}
              />
              <AppBar style={appBarStyle} position="fixed" className={appBarClassName}>
                <Toolbar className={classes.toolbar}>
                  {isLoggedIn && !isTokenUser && (
                    <IconButton
                      color="primary"
                      onClick={handleDrawerMenu}
                      className={classes.menuButton + " menu-toggle"}
                      aria-label="Menu"
                    >
                      <i className="fas fa-bars"></i>
                    </IconButton>
                  )}
                  <img className={classes.logo} src={logoFileName} alt="Logo" />
                  <Typography variant="h6" className={classes.appTitle}>
                    {""}
                  </Typography>
                  {countdown}
                  {sizer}
                  {showLoginButton && (
                    <Button
                      className={classes.appBarButton}
                      color="primary"
                      onClick={handleLogin}
                    >
                      Login
                    </Button>
                  )}
                  {isLoggedIn && showAlerts && !isTokenUser && (
                    <span className={classes.homeIcon}>
                      <AlertButton
                        classes={{
                          alertButton: classes.alertButton,
                          menuButton: classes.menuButton,
                        }}
                        color="primary"
                      />
                    </span>
                  )}
                  {isLoggedIn && !isTokenUser && (
                    <Tooltip enterDelay={500} title="Ctrl+Alt+H">
                      <span className={classes.homeIcon}>
                        <HomeButton classes={{
                          menuButton:classes.menuButton,
                        }} color="primary" />
                      </span>
                    </Tooltip>
                  )}
                  {isLoggedIn && !isTokenUser && (
                    <div>
                      <UserPrefsButton
                        classes={{ menuButton: classes.menuButton }}
                        color="primary"
                        history={history}
                      />
                    </div>
                  )}
                  {onLanguageChange && (
                    <div>
                      <LanguageButton
                        classes={{ menuButton: classes.menuButton }}
                        onLanguageChange={onLanguageChange}
                        options={[]}
                      />
                    </div>
                  )}
                </Toolbar>
              </AppBar>
              <div className={classes.appContents} >
                {props.children}
                {floatingHelpTrigger}
                {expiredModal}
                {!isLoggedIn && footer}
                {/* {floatingSuggestionTrigger} */}
                {bottomPadding}
              </div>
            </div>
          </HotKeyMappingContext.Provider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  );
}
export default withStyles(styles)(AppView);